import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { 
  CartItem, 
  getCurrentCartState, 
  subscribeToCartState, 
  removeFromCart, 
  updateCartItemQuantity,
  getCartItemCount,
  getCartTotal,
  initializeCart
} from '../utils/cartManager';

interface CartPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartPopup: React.FC<CartPopupProps> = ({ isOpen, onClose }) => {
  const [currentCartItems, setCurrentCartItems] = useState<CartItem[]>([]);
  const [itemCount, setItemCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // 加载购物车数据
  const loadCartData = async () => {
    setIsLoading(true);
    try {
      // 初始化购物车数据（从API加载）
      console.log('初始化购物车数据');
      await initializeCart();
      const newCartState = getCurrentCartState();
      setCurrentCartItems(newCartState);
      setItemCount(getCartItemCount());
      setCartTotal(getCartTotal());
    } catch (error) {
      console.error('加载购物车数据失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 监听购物车数据变化
  useEffect(() => {
    const updateCartData = () => {
      const newCartState = getCurrentCartState();
      setCurrentCartItems(newCartState);
      setItemCount(getCartItemCount());
      setCartTotal(getCartTotal());
    };

    // 订阅购物车状态变化
    const unsubscribe = subscribeToCartState(updateCartData);
 
      loadCartData(); 

    return unsubscribe;
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      
      {/* Cart Popup */}
      <div className="fixed top-20 right-0 md:right-8 lg:right-12 z-50">
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-md md:w-96">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              Shopping Cart ({itemCount} items)
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close cart"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Cart Content */}
          <div className="p-3 sm:p-4">
            {itemCount === 0 ? (
              /* Empty Cart */
              <div className="text-center py-6 sm:py-8">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <ShoppingCartIcon className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-3 sm:mb-4 text-sm sm:text-base">Your cart is empty</p>
                <Link
                  to="/products"
                  className="inline-block text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors font-medium text-sm sm:text-base"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                  onClick={onClose}
                >
                  Start Shopping
                </Link>
              </div>
            ) : (
              /* Cart Items */
              <div className="space-y-3 sm:space-y-4">
                {/* Cart Items List */}
                {currentCartItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                      {item.customImage ? (
                        <img 
                          src={item.customImage} 
                          alt={item.product.name}
                          className="w-full h-full object-cover rounded"
                        />
                      ) : (
                        <span className="text-lg sm:text-2xl">📦</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                        {item.product.name}
                      </h4>
                      <p className="text-xs text-gray-500">
                        Size: {item.size || 'Standard'}, Color: {item.color || 'Default'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Qty: {item.quantity}
                      </p>
                      <p className="text-xs sm:text-sm font-medium" style={{ color: '#2e3a47' }}>
                        ${(item.product.basePrice * item.quantity).toFixed(2)}
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        removeFromCart(item.id);
                      }}
                      className="text-red-500 hover:text-red-700 text-xs sm:text-sm transition-colors p-1"
                    >
                      Remove
                    </button>
                  </div>
                ))}

                {/* Cart Total */}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs sm:text-sm font-medium text-gray-900">Subtotal:</span>
                    <span className="text-xs sm:text-sm font-bold text-gray-900">${cartTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* View Cart Button */}
                <Link
                  to="/cart"
                  className="block w-full text-white text-center py-2 sm:py-3 px-4 rounded-lg transition-colors font-bold text-sm sm:text-base"
                  style={{ backgroundColor: '#eb9072' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0a085'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eb9072'}
                  onClick={onClose}
                >
                  View Cart ({itemCount})
                </Link>

                {/* Checkout Button */}
                <Link
                  to="/checkout"
                  className="block w-full text-white py-2 px-4 rounded-lg transition-colors font-medium text-sm sm:text-base text-center"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                  onClick={onClose}
                >
                  Checkout
                </Link>
              </div>
            )}

            {/* Guarantee */}
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                100% Satisfaction Guaranteed
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartPopup; 